import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { userAtom } from '../../../recoil/atoms';
import { fetchThirdPartySession } from '../../../services';

const extractUniversalLearningId = (userData) => {
  if (!userData) return null;
  return (
    userData['universal-learning-id'] ||
    userData.universalLearningId ||
    userData['universal_learning_id'] ||
    null
  );
};

const persistAuthData = (token, userData) => {
  try {
    if (token) {
      localStorage.setItem('token', token);
    }
    if (userData) {
      localStorage.setItem('userData', JSON.stringify(userData));
    }
    const universalLearningId = extractUniversalLearningId(userData);
    if (universalLearningId) {
      localStorage.setItem('universal-learning-id', universalLearningId);
    }
  } catch (error) {
    console.error('❌ Third-Party Session: failed to persist auth data', error);
  }
};

const ThirdPartySession = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const setUser = useSetRecoilState(userAtom);
  const [status, setStatus] = useState({ state: 'loading', message: 'Preparing session...' });

  const sessionToken = useMemo(() => searchParams.get('s'), [searchParams]);

  useEffect(() => {
    let isMounted = true;

    if (!sessionToken) {
      console.warn('❌ Third-Party Session: missing token in URL');
      setStatus({ state: 'error', message: 'Session token not found. Please restart your session.' });
      return () => { isMounted = false; };
    }

    const controller = new AbortController();

    const hydrateSession = async () => {
      console.log('🌐 Third-Party Session: syncing session token...');
      setStatus({ state: 'loading', message: 'Syncing your session...' });

      const response = await fetchThirdPartySession({
        sessionToken,
        signal: controller.signal,
      });

      if (!isMounted) return;

      if (!response.success) {
        const isExpired = response.status === 401;
        setStatus({
          state: isExpired ? 'expired' : 'error',
          message: isExpired
            ? 'Session expired. Please retry.'
            : response.message || 'Unable to start session. Please try again.',
        });
        return;
      }

      const { data } = response;
      const backendUserData = data?.['user-data'] || data?.userData;
      const jwtToken = data?.token;

      if (!backendUserData || !jwtToken) {
        console.error('❌ Third-Party Session: incomplete payload', data);
        setStatus({ state: 'error', message: 'Incomplete session payload. Please try again.' });
        return;
      }

      const hydratedUserData = { ...backendUserData };
      persistAuthData(jwtToken, hydratedUserData);
      setUser(hydratedUserData);

      console.log('✅ Third-Party Session: token stored, redirecting to dashboard...');
      setStatus({ state: 'success', message: 'Session synchronized. Redirecting...' });
      navigate('/learner/dashboard', { replace: true });
    };

    hydrateSession();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [sessionToken, navigate, setUser]);

  const isErrorState = status.state === 'error' || status.state === 'expired';

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md rounded-2xl bg-white dark:bg-slate-800 p-8 shadow-lg">
        <div className="mb-4 text-center">
          <p className="text-lg font-semibold text-slate-800 dark:text-slate-200">
            {status.state === 'loading' && 'Syncing Session'}
            {status.state === 'success' && 'Redirecting You'}
            {status.state === 'expired' && 'Session Expired'}
            {status.state === 'error' && 'Something Went Wrong'}
          </p>
        </div>
        <p className="text-center text-slate-600 dark:text-slate-400">{status.message}</p>
        {isErrorState && (
          <div className="mt-6 flex flex-col gap-3">
            <button
              className="w-full rounded-lg bg-emerald-500 px-4 py-2 font-semibold text-white transition hover:bg-emerald-600"
              onClick={() => navigate('/learner/login')}
            >
              Back to Login
            </button>
            <button
              className="w-full rounded-lg border border-slate-200 dark:border-slate-700 px-4 py-2 font-semibold text-slate-700 dark:text-slate-300 transition hover:border-slate-300"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ThirdPartySession;
